import { useEffect, useState, useRef } from 'react';
import { useUserState } from '../views/components/UserStateProvider';
import { getChatHistory, getDocumentFilenames, serverUrl } from '../data/dataApi';
import { jsonrepair } from "jsonrepair";
import { additionalSourceDocuments, checkInconclusiveResponse, unexpectedError, initialFeedbackDetails, getBotMetadata, formatMessages } from '../views/helpers';
import { v4 as uuidv4 } from 'uuid';
import Message from '../views/components/Message';
import TextSelectionMenu from '../views/components/TextSelectionMenu';
import Sidebar from '../views/components/Sidebar';
import FeedbackModal from '../views/components/modals/FeedbackModal';
import Topbar from '../views/components/Topbar';
import Loading from '../views/components/Loading';
import convert from 'heic-convert/browser';
import ThinkingLog from '../views/components/ThinkingLog';
import ActionsMenu from '../views/components/ActionsMenu';
import Questionnaire from '../views/components/Questionnaire';

const Home = () => {
  const { user } = useUserState();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [disableInput, setDisableInput] = useState(false);
  const [messageKey, setMessageKey] = useState();
  const [feedbackDetails, setFeedbackDetails] = useState(initialFeedbackDetails);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [forkPrompt, setForkPrompt] = useState(localStorage.getItem("assistant_type") || "other");
  const [initialRender, setInitialRender] = useState(true); // for dev mode
  const [messageImage, setMessageImage] = useState(null);
  const [thinkingLogCollapsed, setThinkingLogCollapsed] = useState(() => window.innerWidth < 1440);

  // Handle viewport width changes for thinking log collapse
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1440) {
        setThinkingLogCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [messages, setMessages] = useState([
    {
      type: 'apiMessage',
      message: getBotMetadata(forkPrompt, "message"),
      suggestions: []
    },
  ]);
  const [threadId, setThreadId] = useState(null);
  const [sessionInfo, setSessionInfo] = useState();
  const [disableFeedbackBtn, setDisableFeedbackBtn] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [replyText, setReplyText] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [intentProfile, setIntentProfile] = useState({
    interests: [],
    useCases: [],
    desiredOutcomes: [],
    questionnaireCount: 0
  });

  const chatboxMainRef = useRef(null);
  const feedbackModalRef = useRef(null);
  const textAreaRef = useRef(null);
  const hasMounted = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const currentThinkingBatch = useRef([]);
  const abortControllerRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
    // Reset file input
    e.target.value = '';
  };

  const handleChoosePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };

  const compressImage = (base64Image, maxWidth = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.src = base64Image;
    });
  };

  const processFile = async (file) => {
    if (!file) return;

    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic'];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.heic'];

    const fileName = file.name.toLowerCase();
    const isValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!validTypes.includes(file.type) && !isValidExtension) {
      alert('Please upload a valid image file (.png, .jpg, .jpeg, .webp, .gif, or .heic)');
      return;
    }

    try {
      let base64Image;

      if (file.type === 'image/heic' || fileName.endsWith('.heic')) {
        const arrayBuffer = await file.arrayBuffer();
        const inputBuffer = new Uint8Array(arrayBuffer);

        const outputBuffer = await convert({
          buffer: inputBuffer,
          format: 'JPEG'
        });

        const base64String = btoa(
          Array.from(new Uint8Array(outputBuffer))
            .map(byte => String.fromCharCode(byte))
            .join('')
        );
        base64Image = `data:image/jpeg;base64,${base64String}`;
      } else {
        const reader = new FileReader();
        base64Image = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      // Compress the image before setting
      const compressedImage = await compressImage(base64Image);
      setMessageImage(compressedImage);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files.length > 1) {
        alert('Only one image can be uploaded at a time.');
        return;
      }
      await processFile(files[0]);
    }
  };

  const sendSupportMessage = () => {
    // setMessages(prevState => ([
    //   ...prevState,
    //   {
    //     type: 'supportMessage',
    //     message: "Gelest sales, customer service, technical support and regulatory experts are on-call, ready to help.",
    //     rating: "",
    //     ratingId: null,
    //   },
    // ]));
  };

  const handleBottomScroll = () => {
    isProgrammaticScroll.current = true;
    // Use requestAnimationFrame to ensure scroll happens after DOM layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'instant'
        });
        setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 50);
      });
    });
  };

  const filterUniqueSources = (sources) => {
    const seen = new Set();
    return sources.filter(source => {
      const key = `${source.title}-${source.source_url}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const getFileURL = async (filename, allFilenames = []) => {
    return await fetch(`/sds.json`)
      .then(response => response.json())
      .then(async sdsFiles => {
        if (sdsFiles.includes(filename)) {
          return `https://s3.amazonaws.com/gelest/sds/${filename}`;
        }

        let file = allFilenames.find(row => row.filename === filename);

        if (!file) {
          const replacedFilename = filename.replace(/-/g, "_");
          file = allFilenames.find(row => row.filename === replacedFilename);
          if (file) {
            file.filename = replacedFilename;
          }
        }

        if (file) {
          if (file.source_url) {
            return file.source_url;
          }

          return `https://www.gelest.com/wp-content/uploads/${filename.toLowerCase().includes('msds') ? 'product_msds/' : ''}${filename}`;
        }

        return "";
      });
  };

  const handleSubmit = async (e, promptQuery, instruction, backendQuery = null, intentProfileOverride = null) => {
    e && e.preventDefault();
    setChatError(null);

    if (!query && !promptQuery) {
      if (forkPrompt === 'product') {
        alert('Please select a product from the list');
      } else {
        alert('Please input a question');
      }
      return;
    }

    // Clear questionnaire if one is currently shown
    if (questionnaire) {
      setQuestionnaire(null);
    }

    setDisableInput(true);

    const question = promptQuery || query.trim();
    const questionForApi = backendQuery || question; // Use backendQuery if provided, otherwise use displayed question

    // Create markdown message with reply and/or image if present
    let messageMarkdown = question;
    if (replyText) {
      messageMarkdown = `"${replyText}"\n\n${question}`;
    }
    if (messageImage) {
      messageMarkdown = `![Uploaded Image](${messageImage})\n\n${messageMarkdown}`;
    }

    setMessages(state => ([
      ...state,
      {
        type: 'userMessage',
        message: messageMarkdown,
        replyText: replyText || null,
        originalQuestion: question,
      },
    ]));

    setLoading(true);
    setQuery('');
    setMessageImage(null);
    setReplyText(null);
    if(!thinkingLogCollapsed) {
      setThinking(true);
    }
    currentThinkingBatch.current = [];
    setStatusMessage('');

    try {
      let responseText = "",
        messageStarted = false,
        sources = [],
        partialAnnotation = "",
        citations = [],
        thinkingTextMap = {},
        productsData = [];

      try {
        let guardrailViolation = false;

        // Create abort controller for this request
        abortControllerRef.current = new AbortController();

        const response = await fetch(`${serverUrl}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({
            threadId,
            forkPrompt,
            question: questionForApi,
            messages,
            image: messageImage,
            replyText,
            instruction,
            thinkingLog: !thinkingLogCollapsed,
            intentProfile: intentProfileOverride || intentProfile
          }),
          signal: abortControllerRef.current.signal,
        });

        const supportMessageTimeoutId = setTimeout(() => {
          sendSupportMessage();
        }, 10000);

        const filenamesResponse = await getDocumentFilenames(); // used to get the list of all filenames to check if it's registered in case the LLM hallucinates a filename

        async function streamToString(body) {
          const reader = body?.pipeThrough(new TextDecoderStream()).getReader();
          let buffer = '',
            currentSourcesList = [];

          while (reader) {
            let stream = await reader.read();
            if (stream.done) break;

            buffer += stream.value;

            if (buffer.includes('"id":"cached-response"')) {
              setThreadId(null);
              setLoading(false);
              setDisableInput(false);

              const message = JSON.parse(buffer).choices[0].delta.content;

              responseText += message;

              setMessages((state) => ([
                ...state,
                {
                  type: 'apiMessage',
                  message,
                  rating: "",
                  ratingId: null,
                },
              ]));

              return;
            }

            // Split on }{ to separate multiple JSON objects
            const parts = buffer.split('}{').map((part, index, array) => {
              if (index === 0) return part + '}';
              if (index === array.length - 1) return '{' + part;
              return '{' + part + '}';
            });

            // For single part, check if it's complete JSON by trying to parse
            // For multiple parts, process all except the last (which might be incomplete)
            let partsToProcess = [];
            if (parts.length === 1) {
              // Try to parse single part to see if it's complete
              try {
                JSON.parse(jsonrepair(parts[0]));
                partsToProcess = parts; // It's complete, process it
              } catch (e) {
                // Incomplete JSON, keep in buffer
                partsToProcess = [];
              }
            } else {
              partsToProcess = parts.slice(0, -1);
            }

            for (let i = 0; i < partsToProcess.length; i++) {
              try {
                const parsed = JSON.parse(jsonrepair(partsToProcess[i]));

                // Handle both single objects and arrays
                const results = Array.isArray(parsed) ? parsed : [parsed];

                for (const result of results) {
                  if (result.type === "heartbeat") {
                    // Keep-alive message from server, ignore it
                    continue;
                  }

                  if (result.type === "status") {
                    // Update status message
                    setStatusMessage(result.message || '');
                    continue;
                  }

                  if (result.type === "products") {
                    // Handle product suggestions
                    const products = result.data || [];
                    productsData = products; // Store in closure variable
                    
                    setMessages(messages => {
                      if (messages.length === 0) {
                        return messages;
                      }
                      
                      const lastMessage = messages[messages.length - 1];
                      const newMessage = {
                        ...lastMessage,
                        products: products,
                      };
                      
                      return [...messages.slice(0, messages.length - 1), newMessage];
                    });
                    continue;
                  }

                  if (result.type === "questionSuggestions") {
                    // Handle follow-up question suggestions
                    const questions = result.questions || [];
                    
                    setMessages(messages => {
                      if (messages.length === 0) {
                        return messages;
                      }
                      
                      const lastMessage = messages[messages.length - 1];
                      const newMessage = {
                        ...lastMessage,
                        followUpQuestions: questions,
                      };
                      
                      return [...messages.slice(0, messages.length - 1), newMessage];
                    });
                    continue;
                  }

                  if (result.type === "questionnaire") {
                    // Handle questionnaire for broad queries
                    clearTimeout(supportMessageTimeoutId);
                    setLoading(false);
                    setDisableInput(false);
                    setThinking(false);
                    setStatusMessage('');
                    
                    setQuestionnaire({
                      originalQuery: result.originalQuery,
                      questions: result.questions
                    });
                    
                    // Remove the user message since we're showing questionnaire instead
                    setMessages(prevMessages => prevMessages.slice(0, -1));
                    
                    return;
                  }

                  if (result.type === "guardrail_violation") {
                    // Handle guardrail violation
                    clearTimeout(supportMessageTimeoutId);
                    setLoading(false);
                    setDisableInput(false);
                    setThinking(false);

                    setChatError(result.message || "Your request could not be processed due to content safety policies.");

                    // Remove the last 2 messages (user message + any started API response)
                    setMessages(prevMessages => prevMessages.slice(0, -2));

                    guardrailViolation = true;
                    return;
                  }

                  if (result.type === "response.output_text.delta") {

                    if (!messageStarted) {
                      clearTimeout(supportMessageTimeoutId);
                      setMessages((state) => ([
                        ...state,
                        {
                          type: 'apiMessage',
                          message: "",
                          rating: "",
                          ratingId: null,
                        },
                      ]));

                      messageStarted = true;
                    }

                    if (result.delta?.length) {
                      let messagePart = {
                        value: result.delta,
                      };

                      if (result.content_type === "thinking") {
                        if (!thinkingLogCollapsed) {
                          setThinking(false);

                          if (!thinkingTextMap[result.item_id]) {
                            thinkingTextMap[result.item_id] = "";
                          }

                          thinkingTextMap[result.item_id] += messagePart.value;

                          setThinkingMessages(prevState => {
                            const messageIndex = prevState.findIndex(msg => msg.id === result.item_id);

                            if (messageIndex === -1) return prevState;

                            const newMessages = [...prevState];
                            newMessages[messageIndex] = {
                              ...newMessages[messageIndex],
                              message: thinkingTextMap[result.item_id],
                            };

                            return newMessages;
                          });

                          // Also update the current batch ref
                          const batchIndex = currentThinkingBatch.current.findIndex(msg => msg.id === result.item_id);
                          if (batchIndex !== -1) {
                            currentThinkingBatch.current[batchIndex] = {
                              ...currentThinkingBatch.current[batchIndex],
                              message: thinkingTextMap[result.item_id],
                            };
                          }
                        }

                        continue;
                      }

                      const getSourceMetadata = async () => {
                        partialAnnotation = "";

                        const { file_id } = messagePart.annotations[0].file_citation;

                        const response = await fetch(`${serverUrl}/api/file/${file_id}`, {
                          method: "GET",
                          headers: {
                            "Content-Type": "application/json",
                          },
                        });

                        const { data } = await response.json();

                        if (data.rows.length) {
                          const { title, source_url } = data.rows[0];

                          sources.push({ title, source_url });

                          // Avoid listing duplicate sources within the same list
                          if (!currentSourcesList.some(source => source.title === title && source.source_url === source_url)) {
                            currentSourcesList.push({ title, source_url });

                            return `<a href="${source_url}" class="source-document-pill" target="_blank"><img src="${serverUrl}/static/assets/retrieve-document-fork.svg" alt="Document icon" />${title}</a>`;
                          } else {
                            return "";
                          }
                        } else {
                          return "";
                        }
                      };

                      if (/【[^】]*】/g.test(messagePart.value)) {
                        messagePart.value = await getSourceMetadata();
                      } else if (/【/g.test(messagePart.value) || partialAnnotation) { // Handles partially streamed annotation
                        if (/【[^】]*】/g.test(partialAnnotation)) {
                          messagePart.value = await getSourceMetadata();
                        } else {
                          partialAnnotation += messagePart.value;
                          messagePart.value = "";
                        }
                      } else {
                        currentSourcesList = [];
                      }

                      const citationRegex = /(?:```json\s*)?\{\s*"citations"\s*:\s*\[\s*(\{\s*"filename"\s*:\s*"[^"]*"\s*,\s*"title"\s*:\s*"[^"]*"\s*}[\s\S]*?,?\s*)*\s*\]\s*\}(?:```)?/gm;

                      if (citationRegex.test(messagePart.value)) {
                        const match = messagePart.value.match(citationRegex);
                        if (match) {
                          let citationJSON = match[0];

                          if (/```json/.test(citationJSON)) {
                            citationJSON = citationJSON
                              .replace(/```json/g, "")
                              .replace(/```/g, "");
                          }

                          try {
                            citations = JSON.parse(citationJSON).citations;
                          } catch (error) {
                            console.error('Failed to parse JSON:', error);
                          }
                        }

                        partialAnnotation = "";
                        messagePart.value = "";
                      } else if (/{/g.test(messagePart.value) || partialAnnotation) {
                        partialAnnotation += messagePart.value;

                        if (citationRegex.test(partialAnnotation)) {
                          const match = partialAnnotation.match(citationRegex);
                          if (match) {
                            let citationJSON = match[0];

                            if (/```json/.test(citationJSON)) {
                              citationJSON = citationJSON
                                .replace(/```json/g, "")
                                .replace(/```/g, "");
                            }

                            try {
                              citations = JSON.parse(citationJSON).citations;
                            } catch (error) {
                              console.error('Failed to parse JSON:', error);
                            }
                          }

                          partialAnnotation = "";
                        }

                        messagePart.value = "";
                      }

                      if (citations.length) {
                        const filteredCitations = citations.filter((value, index, self) =>
                          index === self.findIndex((t) => JSON.stringify(t) === JSON.stringify(value))
                        );
                        const technicalSiteCitations = [];

                        for (let i = 0; i < filteredCitations.length; i++) {
                          const { filename } = filteredCitations[i];

                          if (additionalSourceDocuments[filename]) {
                            technicalSiteCitations.push(...additionalSourceDocuments[filename]);
                          }
                        }

                        const citationLinks = await Promise.all(filteredCitations.map(async ({ filename, title, url }) => {
                          if (filenamesResponse.success) {
                            const fileURL = await getFileURL(filename, filenamesResponse.data.rows);

                            if (fileURL) {
                              return `<a href="${url ? url : fileURL}" class="source-document-pill" target="_blank">
                              <img src="${serverUrl}/static/assets/retrieve-document-fork.svg" alt="Document icon" />${title}
                          </a>`;
                            } else {
                              return;
                            }
                          }

                          return;
                        }));

                        messagePart.value = `<div class="citations-wrapper d-flex">
                                      ${citationLinks.join("")}
                                      ${technicalSiteCitations.length ? '<p class="technical-library-title"><strong>Results from the Gelest Technical Library:</strong></p>' : ''}
                                      ${technicalSiteCitations.map(({ url }) =>
                          `<a href="${url}" class="source-document-pill" target="_blank">
                                          <img src="${serverUrl}/static/assets/molecule-icon.svg" alt="Molecule icon" />View Technical Page
                                        </a>`).join("")}
                                    </div>`;

                        citations = [];
                      }

                      responseText += messagePart.value.replace(/`/g, '').replace('json', '');

                      setLoading(false);

                      setMessages(messages => {
                        const lastMessage = messages[messages.length - 1];
                        const newMessage = {
                          ...lastMessage,
                          sources: filterUniqueSources(sources),
                          message: responseText,
                        };

                        return [...messages.slice(0, messages.length - 1), newMessage];
                      });
                    }
                  } else if (result.content_type === "thinking") {
                    if (result.type === "response.content_part.added") {
                      const newThinkingItem = {
                        id: result.item_id,
                        message: "",
                      };
                      setThinkingMessages(prevState => ([
                        ...prevState,
                        newThinkingItem,
                      ]));
                      // Also add to the current batch
                      currentThinkingBatch.current.push(newThinkingItem);
                    } else if (result.type === "response.output_item.done") {
                      delete thinkingTextMap[result.item_id];
                    }
                  }
                }
              } catch (error) {
                console.error("Error parsing JSON:", error);
                console.error("Stream value causing error:", partsToProcess[i]);
              }
            }

            // Update buffer: if we processed a single complete part, clear buffer
            // Otherwise keep the last incomplete part
            if (partsToProcess.length > 0 && parts.length === 1) {
              buffer = ''; // Processed complete single JSON
            } else {
              buffer = parts[parts.length - 1]; // Keep incomplete part
            }
          }
        }

        await streamToString(response.body);

        // Don't save message if guardrail was triggered
        if (guardrailViolation) {
          return;
        }

        if (checkInconclusiveResponse(responseText) && !messages.some(message => message.type === "supportMessage")) {
          const botSettingsRes = await fetch(`${serverUrl}/api/bot-settings`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const botSettings = await botSettingsRes.json();

          if (botSettings.success && messages.filter(message => message.type === "apiMessage").length >= Number(botSettings.data.rows.find(row => row.type === "supportMessageThreshold").value)) {
            sendSupportMessage();
          }
        }

        if (process.env.REACT_APP_ENV !== "development") {
          const dbResponse = await fetch(`${serverUrl}/api/save-message`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              session_id: sessionInfo.id,
              user_message: question,
              api_response: responseText,
              gptVersion: "o4-mini",
              temperature: 0,
              time: new Date().toISOString(),
              user_ip: sessionInfo.userIp,
              source_docs: JSON.stringify(sources.map(doc => doc.source_url)),
              origin: sessionInfo.origin,
              account: typeof GELEST_GPT !== 'undefined' ? GELEST_GPT : null, // eslint-disable-line no-undef
              email: user.email,
              bot_type: 'navigator',
              unanswered: checkInconclusiveResponse(responseText),
              thinkingMessages: JSON.stringify(currentThinkingBatch.current),
              products: productsData,
            }),
          });

          const dbData = await dbResponse.json();

          if (dbData.error) {
            setChatError(unexpectedError);
          } else if (dbData.data?.rows?.[0]?.id) {
            setMessages(prevState => {
              const lastMessage = prevState[prevState.length - 1];
              const updatedMessage = {
                ...lastMessage,
                id: dbData.data.rows[0].id,
              };

              return [...prevState.slice(0, prevState.length - 1), updatedMessage];
            });
          }
        }

        setDisableInput(false);
      } catch (error) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error:', error);
        setChatError(unexpectedError);
      }

    } catch (error) {
      setLoading(false);
      setChatError(unexpectedError);
      console.error('error', error);
    }
  };

  useEffect(() => {
    if (messages.length <= 1) {
      (async () => {
        if (process.env.REACT_APP_ENV !== "development") {
          const response = await getChatHistory(sessionInfo?.id || "latest");

          if (response.success && response.data.rows && response.data.rows.length) {
            setMessages(formatMessages(response.data.rows.sort((a, b) => Number(a.id) - Number(b.id))));
            setSessionInfo(prevState => ({
              ...prevState,
              id: response.data.rows[0].session_id,
            }));
          }
        }

        await fetch('https://api.ipify.org?format=json')
          .then(response => response.json())
          .then(data => {
            setSessionInfo(prevState => ({
              ...prevState,
              id: prevState?.id || uuidv4(),
              userIp: data.ip
            }));
          });
      })();
    }
  }, [messages]);

  const handleEnter = e => {
    if (e.key === 'Enter' && query) {
      handleSubmit(e);
    } else if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const clearChat = (forkPrompt = "document_retrieval") => {
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setMessages([
      {
        type: 'apiMessage',
        message: getBotMetadata(forkPrompt, "message"),
        suggestions: []
      },
    ]);
    setSessionInfo(prevState => ({ ...prevState, id: uuidv4() }));
    setMessageImage(null);
    setThinkingMessages([]);
    setChatError(null);
    setLoading(false);
    setDisableInput(false);
    setThinking(false);
    setStatusMessage('');
    setQuestionnaire(null);
    setIntentProfile({
      interests: [],
      useCases: [],
      desiredOutcomes: [],
      questionnaireCount: 0
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScroll.current) {
        return; // Ignore programmatic scrolls
      }

      const threshold = 5; // Allow small threshold for floating point errors
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < threshold;

      if (isAtBottom) {
        setUserScrolled(false); // User scrolled back to bottom, resume auto-scroll
      } else {
        setUserScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Reset userScrolled when response starts or ends to enable auto-scroll
    setUserScrolled(false);
  }, [disableInput]);

  useEffect(() => {
    if (!userScrolled) {
      handleBottomScroll();
    }
  }, [messages, userScrolled]);

  // Use MutationObserver to continuously scroll while content changes during streaming
  useEffect(() => {
    if (disableInput && !userScrolled) {
      const observer = new MutationObserver(() => {
        if (!userScrolled) {
          handleBottomScroll();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true, // Also watch for attribute changes (e.g., style updates)
      });

      return () => observer.disconnect();
    }
  }, [disableInput, userScrolled]);

  useEffect(() => {
    if (hasMounted.current && !initialRender) {
      clearChat(forkPrompt);
    } else {
      hasMounted.current = true;
      setInitialRender(false);
    }
  }, [forkPrompt]);

  // Handle questionnaire submission
  const handleQuestionnaireSubmit = async (originalQuery, answers) => {
    setQuestionnaire(null);
    
    // Compute the new profile synchronously (can't rely on async setState)
    const computeNewProfile = (prev) => {
      const newProfile = { ...prev };
      
      answers.forEach(({ question, answer }) => {
        const questionLower = question.toLowerCase();
        
        // Categorize the answer based on question type
        if (questionLower.includes('interest') || questionLower.includes('aspect') || questionLower.includes('topic') || questionLower.includes('looking for')) {
          if (!newProfile.interests.includes(answer)) {
            newProfile.interests = [...newProfile.interests, answer];
          }
        } else if (questionLower.includes('application') || questionLower.includes('use case') || questionLower.includes('industry') || questionLower.includes('working on')) {
          if (!newProfile.useCases.includes(answer)) {
            newProfile.useCases = [...newProfile.useCases, answer];
          }
        } else if (questionLower.includes('outcome') || questionLower.includes('looking to') || questionLower.includes('goal') || questionLower.includes('help with')) {
          if (!newProfile.desiredOutcomes.includes(answer)) {
            newProfile.desiredOutcomes = [...newProfile.desiredOutcomes, answer];
          }
        } else {
          // Default to interests for uncategorized questions
          if (!newProfile.interests.includes(answer)) {
            newProfile.interests = [...newProfile.interests, answer];
          }
        }
      });
      
      newProfile.questionnaireCount = prev.questionnaireCount + 1;
      
      return newProfile;
    };
    
    // Compute the updated profile
    const updatedProfile = computeNewProfile(intentProfile);
    
    // Update state for future renders
    setIntentProfile(updatedProfile);
    
    // Build an enriched query with the user's answers
    const answerContext = answers
      .map((a, i) => `${a.question}: ${a.answer}`)
      .join('\n');
    
    const enrichedQuery = `${originalQuery}\n\n${answerContext}`;
    
    // Pass the updated profile directly to avoid race condition
    handleSubmit(null, enrichedQuery, 'skipQuestionnaire', null, updatedProfile);
  };

  // Handle questionnaire skip
  const handleQuestionnaireSkip = async (originalQuery) => {
    setQuestionnaire(null);
    
    // Just proceed with the original query, skip the questionnaire
    handleSubmit(null, originalQuery, 'skipQuestionnaire');
  };

  return (
    <>
      <div
        id="home"
        className={`d-flex ${thinkingLogCollapsed ? '' : 'with-thinking-log'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="drag-overlay position-fixed w-100 h-100 d-flex align-items-center justify-content-center">
            <div className="tif-color-white text-center">
              <p className="tif-mb-0">Drop image here</p>
            </div>
          </div>
        )}
        <Sidebar />
        <div className="flex-grow-1 w-100">
          <Topbar title="Gelest Navigator AI Assistant" forkPrompt={forkPrompt} onForkPromptChange={setForkPrompt} onClearChat={clearChat} onCollapse={setThinkingLogCollapsed} collapsed={thinkingLogCollapsed} />
          <div className="content-wrapper d-flex tif-pb-md-130 tif-pb-95" ref={chatboxMainRef}>
            <div className="messages-wrapper">
              <TextSelectionMenu onReply={(selectedText) => {
                setReplyText(selectedText);
                const inputElement = document.getElementById('user-input');
                if (inputElement) {
                  inputElement.focus();
                }
              }} />
              {
                messages.map((message, index) => (
                  <Message
                    message={message}
                    messageIndex={index}
                    setMessageKey={setMessageKey}
                    messages={messages}
                    setMessages={setMessages}
                    sessionInfo={sessionInfo}
                    feedbackDetails={feedbackDetails}
                    onFeedbackDetailsChange={setFeedbackDetails}
                    onFeedbackOpen={setFeedbackOpen}
                    onChatError={setChatError}
                    key={index}
                    disableInput={disableInput}
                    onSubmit={handleSubmit}
                    setQuery={setQuery}
                    loading={loading}
                    userEmail={user.email}
                    botType={forkPrompt}
                  />
                ))
              }

              {questionnaire && (
                <Questionnaire
                  questions={questionnaire.questions}
                  originalQuery={questionnaire.originalQuery}
                  onSubmit={handleQuestionnaireSubmit}
                  onSkip={handleQuestionnaireSkip}
                  loading={loading}
                />
              )}

              {loading ? <Loading statusMessage={statusMessage} /> : <></>}

              <form onSubmit={handleSubmit} id="user-input-form" className="tif-bg-color-white tif-pb-md-35 tif-pb-15 position-fixed">
                <div className="form-wrapper tif-bg-color-golf tif-mx-auto">
                  {chatError && <p className="error-message position-absolute tif-m-0 tif-pl-15">{chatError}</p>}
                  {replyText && (
                    <div className="tif-px-15 tif-pt-15 tif-pb-5">
                      <div className="reply-text-wrapper position-relative tif-p-15">
                        <button
                          className="remove-reply-btn position-absolute border-0"
                          onClick={() => setReplyText(null)}
                        >
                          <img src={`${serverUrl}/static/assets/close-icon.svg`} className="d-block" alt="Close icon" />
                        </button>
                        <p className="tif-mb-0 tif-font-sm reply-text-content">"{replyText}"</p>
                      </div>
                    </div>
                  )}
                  {messageImage && (
                    <div className="tif-px-15 tif-pt-15 tif-pb-5">
                      <div className="message-image-wrapper position-relative overflow-hidden">
                        <button
                          className="remove-image-btn position-absolute border-0"
                        >
                          <img src={`${serverUrl}/static/assets/close-icon.svg`} className="d-block" alt="Close icon" onClick={() => setMessageImage(null)} />
                        </button>
                        <img src={messageImage} className="w-100 h-100 object-fit-cover" alt="Converted from HEIC" />
                      </div>
                    </div>
                  )}

                  <div className="input-wrapper position-relative">
                    <input
                      disabled={loading || disableInput}
                      onKeyDown={handleEnter}
                      ref={textAreaRef}
                      autoFocus
                      id="user-input"
                      className="bg-transparent border-0 fw-normal tif-color-white"
                      placeholder={
                        (loading || disableInput)
                          ? 'Waiting for response...'
                          : getBotMetadata(forkPrompt, "inputPlaceholder")
                      }
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />

                    <ActionsMenu
                      wrapperClassName="position-absolute top-50 translate-middle-y"
                      trigger={
                        <button
                          type="button"
                          id="user-input-attatch-btn"
                          className="bg-transparent border-0 tif-py-0 tif-px-md-0 tif-px-15"
                          disabled={loading || disableInput}
                        >
                          +
                        </button>
                      }
                    >
                      {({ onItemClick }) => (
                        <>
                          <button
                            type="button"
                            className="w-100 text-start border-0"
                            onClick={onItemClick(handleChoosePhoto)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-library-photo"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M7 5.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667l0 -8.666" /><path d="M4.012 7.26a2.005 2.005 0 0 0 -1.012 1.737v10c0 1.1 .9 2 2 2h10c.75 0 1.158 -.385 1.5 -1" /><path d="M17 7h.01" /><path d="M7 13l3.644 -3.644a1.21 1.21 0 0 1 1.712 0l3.644 3.644" /><path d="M15 12l1.644 -1.644a1.21 1.21 0 0 1 1.712 0l2.644 2.644" /></svg>
                            Choose from photos
                          </button>
                          <button
                            type="button"
                            className="w-100 text-start border-0"
                            onClick={onItemClick(handleTakePhoto)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-camera"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2" /><path d="M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /></svg>
                            Take a photo
                          </button>
                        </>
                      )}
                    </ActionsMenu>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".png,.jpg,.jpeg,.webp,.gif,.heic,image/png,image/jpeg,image/webp,image/gif,image/heic"
                      className="d-none"
                    />
                    <input
                      type="file"
                      ref={cameraInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      capture
                      className="d-none"
                    />
                    <button
                      type="button"
                      id="user-input-clear-btn"
                      className="position-absolute bg-transparent border-0 tif-p-0 top-50 translate-middle-y d-md-block d-none"
                      onClick={() => clearChat(forkPrompt)}
                      disabled={loading}
                    >
                      <img src={`${serverUrl}/static/assets/reload-icon.svg`} alt="Reload icon" />
                    </button>
                    <button
                      type="submit"
                      id="user-input-submit-btn"
                      className="position-absolute bg-transparent border-0 tif-p-0"
                      disabled={loading}
                    >
                      <img src={`${serverUrl}/static/assets/paper-plane-icon.svg`} alt="Paper plane icon" />
                    </button>
                  </div>
                </div>
                <p className="copyright-text text-center tif-mt-md-20 tif-mt-15 tif-mb-0 w-100">&copy; Gelest Inc. All Rights Reserved.</p>
              </form>
            </div>
            <ThinkingLog
              thinking={thinking}
              thinkingMessages={thinkingMessages}
            />
          </div>
        </div>
      </div>
      <FeedbackModal
        isOpen={feedbackOpen}
        onOpen={setFeedbackOpen}
        onChange={setFeedbackDetails}
        feedbackDetails={feedbackDetails}
        disabled={disableFeedbackBtn}
        error={feedbackError}
        messageKey={messageKey}
        sessionInfo={sessionInfo}
        onError={setFeedbackError}
        onSuccess={setFeedbackSuccess}
        onUpdate={setMessages}
        onDisable={setDisableFeedbackBtn}
        success={feedbackSuccess}
        messages={messages}
        ref={feedbackModalRef}
        userEmail={user.email}
      />
    </>
  );
};

export default Home;
